import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Box,
  Chip,
  SxProps,
  Theme,
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { Donor } from '@/types';

interface DonorCardProps {
  donor: Donor;
  onSelect?: (donor: Donor) => void;
  onDeselect?: () => void;
  onEdit?: (donor: Donor) => void;
  showActions?: boolean;
  selected?: boolean;
  disabled?: boolean;
  variant?: 'default' | 'compact' | 'detailed';
  sx?: SxProps<Theme>;
}

export const DonorCard: React.FC<DonorCardProps> = ({
  donor,
  onSelect,
  onDeselect,
  onEdit,
  showActions = true,
  selected = false,
  disabled = false,
  variant = 'default',
  sx,
}) => {
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'compact':
        return {
          card: { p: 1.5 },
          content: { '&:last-child': { pb: 1.5 } },
          avatar: { width: 32, height: 32, fontSize: '0.875rem' },
          name: { fontSize: '0.875rem', fontWeight: 500 },
          secondary: { fontSize: '0.75rem' },
        };
      case 'detailed':
        return {
          card: { p: 2 },
          content: { '&:last-child': { pb: 2 } },
          avatar: { width: 48, height: 48, fontSize: '1.125rem' },
          name: { fontSize: '1.125rem', fontWeight: 600 },
          secondary: { fontSize: '0.875rem' },
        };
      default:
        return {
          card: { p: 1.5 },
          content: { '&:last-child': { pb: 1.5 } },
          avatar: { width: 40, height: 40, fontSize: '1rem' },
          name: { fontSize: '1rem', fontWeight: 500 },
          secondary: { fontSize: '0.875rem' },
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <Card
      sx={{
        mb: 1,
        border: selected ? 2 : 1,
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'primary.50' : 'background.paper',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.2s ease-in-out',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': disabled ? {} : {
          borderColor: 'primary.main',
          boxShadow: 2,
        },
        ...sx,
      }}
      onClick={disabled ? undefined : () => {
        if (selected && onDeselect) {
          onDeselect();
        } else if (onSelect) {
          onSelect(donor);
        }
      }}
    >
      <CardContent sx={{
        ...styles.content,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {/* Avatar */}
          <Avatar
            sx={{
              ...styles.avatar,
              bgcolor: selected ? 'primary.main' : 'grey.300',
              color: selected ? 'primary.contrastText' : 'text.secondary',
            }}
          >
            {getInitials(donor.firstName, donor.lastName)}
          </Avatar>

          {/* Informazioni donatore */}
          <Box sx={{ 
            flex: 1, 
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}>
            <Typography
              variant="h6"
              sx={{
                ...styles.name,
                color: selected ? 'primary.main' : 'text.primary',
                mb: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {donor.firstName} {donor.lastName}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                ...styles.secondary,
                color: 'text.secondary',
                mb: donor.phone ? 0.5 : 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {donor.email}
            </Typography>

            {donor.phone && (
              <Typography
                variant="body2"
                sx={{
                  ...styles.secondary,
                  color: 'text.secondary',
                  mb: 1,
                }}
              >
                {donor.phone}
              </Typography>
            )}

            {/* Indirizzo se disponibile e variant detailed */}
            {variant === 'detailed' && donor.address && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  mb: 1,
                }}
              >
                {donor.address}
                {donor.city && `, ${donor.city}`}
                {donor.postalCode && ` ${donor.postalCode}`}
              </Typography>
            )}

            {/* PEC se disponibile e variant detailed */}
            {variant === 'detailed' && donor.pec && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  mb: 1,
                }}
              >
                PEC: {donor.pec}
              </Typography>
            )}

            {/* Spacer per spingere le azioni in fondo */}
            <Box sx={{ flex: 1 }} />

            {/* Azioni */}
            {showActions && (
              <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                {selected && onDeselect ? (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    disabled={disabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeselect();
                    }}
                    sx={{
                      minWidth: 80,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                    }}
                  >
                    Deseleziona
                  </Button>
                ) : onSelect ? (
                  <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    disabled={disabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(donor);
                    }}
                    sx={{
                      minWidth: 80,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                    }}
                  >
                    Seleziona
                  </Button>
                ) : null}
                
                {onEdit && (
                  <Button
                    variant="text"
                    size="small"
                    color="secondary"
                    disabled={disabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(donor);
                    }}
                    sx={{
                      fontSize: '0.75rem',
                      textTransform: 'none',
                    }}
                  >
                    Modifica
                  </Button>
                )}
              </Box>
            )}
          </Box>

          {/* Status chip se selezionato */}
          {selected && (
            <Chip
              label="Selezionato"
              color="primary"
              size="small"
              sx={{
                fontSize: '0.75rem',
                height: 24,
              }}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

interface DonorListProps {
  donors: Donor[];
  onSelect?: (donor: Donor) => void;
  onDeselect?: () => void;
  onEdit?: (donor: Donor) => void;
  selectedDonorId?: string;
  showActions?: boolean;
  variant?: 'default' | 'compact' | 'detailed';
  emptyMessage?: string;
  layout?: 'vertical' | 'horizontal';
  sx?: SxProps<Theme>;
}

export const DonorList: React.FC<DonorListProps> = ({
  donors,
  onSelect,
  onDeselect,
  onEdit,
  selectedDonorId,
  showActions = true,
  variant = 'default',
  emptyMessage = 'Nessun donatore trovato',
  layout = 'vertical',
  sx,
}) => {
  if (donors.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          color: 'text.secondary',
          ...sx,
        }}
      >
        <PersonIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
        <Typography variant="body1">{emptyMessage}</Typography>
      </Box>
    );
  }

  // Se ci sono più donatori e layout è horizontal, usa scroll orizzontale
  const useHorizontalLayout = layout === 'horizontal' && donors.length > 1;

  if (useHorizontalLayout) {
    return (
      <Box
        sx={{
          width: '100%',
          height: variant === 'compact' ? 200 : variant === 'detailed' ? 250 : 220,
          overflow: 'hidden', // Previene scroll della pagina
          position: 'relative',
          ...sx,
        }}
      >
        {/* Box dedicato allo scroll orizzontale */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            position: 'absolute',
            top: 0,
            left: 0,
            // Scrollbar personalizzata
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'grey.100',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'grey.400',
              borderRadius: 4,
              '&:hover': {
                backgroundColor: 'grey.500',
              },
            },
            // Scrollbar per Firefox
            scrollbarWidth: 'thin',
            scrollbarColor: 'grey.400 grey.100',
          }}
        >
          {/* Container flex per le card */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              padding: 1,
              height: '100%',
              minWidth: 'max-content',
            }}
          >
            {donors.map((donor) => (
              <Box
                key={donor.id}
                sx={{
                  width: variant === 'compact' ? 280 : variant === 'detailed' ? 320 : 300,
                  height: '100%',
                  flexShrink: 0,
                }}
              >
                <DonorCard
                  donor={donor}
                  onSelect={onSelect}
                  onDeselect={onDeselect}
                  onEdit={onEdit}
                  showActions={showActions}
                  selected={selectedDonorId === donor.id}
                  variant={variant}
                  sx={{ height: '100%' }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  // Layout verticale (default)
  return (
    <Box sx={sx}>
      {donors.map((donor) => (
        <DonorCard
          key={donor.id}
          donor={donor}
          onSelect={onSelect}
          onDeselect={onDeselect}
          onEdit={onEdit}
          showActions={showActions}
          selected={selectedDonorId === donor.id}
          variant={variant}
        />
      ))}
    </Box>
  );
};
